// Lead Nurture — Sales Command Centre (redesign, Jun 2026).
// Premium, fast, intelligent. Dashboard metric tiles, a TODAY working screen,
// a scannable lead list with Next Best Action + temperature + signals, smart
// filters + preset chips, fast search, and a slide-in client drawer.
//
// NOTE: named exports (computeReadiness, prepChecklist, computeStage,
// NURTURE_STAGES, ReadinessDial) are consumed by LeadDetailPage.js — keep them.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  Users, ChevronLeft, Search, Loader2, AlertCircle, Flame, Sparkles, CalendarDays,
  TrendingUp, Filter, Clock, ChevronRight, CheckCircle2, ArrowUpRight,
  UserPlus, ShieldCheck, Sheet, Radar, Reply, Target, ExternalLink, Rocket,
  PhoneCall, Zap, SlidersHorizontal, X, ArrowDownUp, Columns3, Activity,
  MessageSquare, Mail, StickyNote, ArrowRight,
  Eraser,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth } from '../contexts/AuthContext';
import NewLeadDialog from '../components/nurture/NewLeadDialog';
import GoogleSheetsDialog from '../components/nurture/GoogleSheetsDialog';
import StartOnboardingDialog from '../components/onboarding/StartOnboardingDialog';
import { LeadCard, STATUS_LABELS } from '../components/nurture/command/LeadCard';
import { PipelineBoard } from '../components/nurture/command/PipelineBoard';
import { ClearFollowUpsDialog } from '../components/nurture/command/ClearFollowUpsDialog';
import {
  API, PRESETS, presetPredicate, METRIC_TO_PRESET, effectiveTemp, TEMP_META, isPreReset,
  priorityScore, computePipelineStage, STAGE_META, fmtRel, daysUntil, daysSince,
  nurtureReturned, PIPELINE_GOAL_KEY, fmtMoney,
} from '../components/nurture/command/helpers';

// ═══════════════════════════════════════════════════════════════════
// LEGACY NAMED EXPORTS (consumed by LeadDetailPage.js) — keep intact
// ═══════════════════════════════════════════════════════════════════
export const computeReadiness = (l) => {
  if (!l) return 0;
  const upd = daysSince(l.updated_at);
  const recentProfile = upd !== null && upd <= 7;
  const cliUpd = daysSince(l.last_client_response_at || l.last_response_at);
  const recentClient = cliUpd !== null && cliUpd <= 7;
  const discovery = !!l.discovery_call_at;
  const portalSent = !!l.portal_sent_at;
  const portalDone = !!l.portal_completed_at;
  return (recentProfile ? 30 : 0) + (recentClient ? 30 : 0) + (discovery ? 20 : 0) + (portalSent ? 10 : 0) + (portalDone ? 10 : 0);
};
export const prepChecklist = (l) => ([
  { label: 'Discovery call completed', done: !!l?.discovery_call_at },
  { label: 'Portal invite sent',       done: !!l?.portal_sent_at },
  { label: 'Portal onboarding complete', done: !!l?.portal_completed_at },
  { label: 'Broker assigned',          done: !!l?.assigned_broker_id },
]);
export const NURTURE_STAGES = {
  new_lead:              { label: 'New lead',            cls: 'bg-slate-50 text-slate-700 ring-slate-200',      dot: 'bg-slate-400' },
  contacted:             { label: 'Contacted',           cls: 'bg-blue-50 text-blue-700 ring-blue-200/60',      dot: 'bg-blue-500' },
  engaged:               { label: 'Engaged',             cls: 'bg-cyan-50 text-cyan-700 ring-cyan-200/60',      dot: 'bg-cyan-500' },
  discovery_booked:      { label: 'Discovery booked',    cls: 'bg-violet-50 text-violet-700 ring-violet-200/60',dot: 'bg-violet-500' },
  discovery_done:        { label: 'Discovery done',      cls: 'bg-indigo-50 text-indigo-700 ring-indigo-200/60',dot: 'bg-indigo-500' },
  portal_active:         { label: 'Portal onboarding',   cls: 'bg-amber-50 text-amber-800 ring-amber-200/60',   dot: 'bg-amber-500' },
  awaiting_docs:         { label: 'Awaiting docs',       cls: 'bg-orange-50 text-orange-800 ring-orange-200/60',dot: 'bg-orange-500' },
  ready_for_broker:      { label: 'Ready for broker',    cls: 'bg-emerald-50 text-emerald-700 ring-emerald-200/60', dot: 'bg-emerald-500' },
  cold:                  { label: 'Cold',                cls: 'bg-slate-100 text-slate-500 ring-slate-200',     dot: 'bg-slate-400' },
  disqualified:          { label: 'Disqualified',        cls: 'bg-rose-50 text-rose-700 ring-rose-200/60',      dot: 'bg-rose-500' },
  moved_to_client_portal:{ label: 'Moved to Client Portal', cls: 'bg-slate-800 text-white ring-slate-700',       dot: 'bg-emerald-400' },
};
export const computeStage = (l) => {
  if (!l) return 'new_lead';
  if (l.status?.startsWith('closed_') || l.status === 'lost') return 'disqualified';
  if (l.status === 'moved_to_client_portal') return 'moved_to_client_portal';
  if (l.status === 'graduated') return 'ready_for_broker';
  const readiness = computeReadiness(l);
  if (readiness === 100) return 'ready_for_broker';
  if (l.portal_completed_at) return 'ready_for_broker';
  if (l.portal_sent_at && !l.portal_completed_at) return 'awaiting_docs';
  if (l.discovery_call_at) return 'discovery_done';
  if (l.next_action?.toLowerCase()?.includes('discovery')) return 'discovery_booked';
  const cliDs = daysSince(l.last_client_response_at || l.last_response_at);
  if (cliDs !== null && cliDs <= 3) return 'engaged';
  const upd = daysSince(l.last_contacted_at);
  const created = daysSince(l.created_at);
  if (created !== null && created > 14 && (upd === null || upd > 14)) return 'cold';
  if (upd !== null) return 'contacted';
  return 'new_lead';
};
export const ReadinessDial = ({ score, tone, size = 64 }) => {
  const stroke = size >= 100 ? 10 : 5;
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - (Math.min(100, Math.max(0, score)) / 100) * circ;
  const numSize = size >= 100 ? 'text-4xl' : 'text-sm';
  const lblSize = size >= 100 ? 'text-[9px]' : 'text-[8px]';
  return (
    <div className={`relative rounded-full ${tone.bg}`} style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} stroke="rgba(15,23,42,0.06)" strokeWidth={stroke} fill="none" />
        <circle cx={size / 2} cy={size / 2} r={r} stroke={tone.ring} strokeWidth={stroke} fill="none"
          strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 0.9s ease-out' }} />
      </svg>
      <div className={`absolute inset-0 flex flex-col items-center justify-center ${tone.text}`}>
        <span className={`${numSize} font-black leading-none`}>{score}</span>
        <span className={`${lblSize} font-black uppercase tracking-widest leading-none mt-0.5`}>ready</span>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// METRIC CARD DEFINITIONS
// ═══════════════════════════════════════════════════════════════════
const METRICS = [
  { key: 'new_today', label: 'New Leads Today', color: 'blue' },
  { key: 'requires_action', label: 'Requiring Action', color: 'rose' },
  { key: 'due_today', label: 'Follow-Ups Due Today', color: 'amber' },
  { key: 'overdue', label: 'Overdue Follow-Ups', color: 'red' },
  { key: 'discovery_booked', label: 'Discovery Booked', color: 'emerald' },
  { key: 'portal_sent', label: 'Portal Invites Sent', color: 'indigo' },
  { key: 'portal_not_started', label: 'Portal Not Started', color: 'orange' },
  { key: 'portal_started', label: 'Portal Started', color: 'sky' },
  { key: 'hot_opps', label: 'Hot Opportunities', color: 'rose' },
  { key: 'converted_week', label: 'Converted This Week', color: 'emerald' },
  { key: 'moved_to_portal', label: 'Moved to Client Portal', color: 'teal' },
  { key: 'lost_closed', label: 'Leads Lost / Closed', color: 'slate' },
];
const COLOR_TONE = {
  blue: 'text-blue-600', rose: 'text-rose-600', amber: 'text-amber-600', red: 'text-red-600',
  emerald: 'text-emerald-600', indigo: 'text-indigo-600', orange: 'text-orange-600',
  sky: 'text-sky-600', teal: 'text-teal-600', slate: 'text-slate-500',
};

// ═══════════════════════════════════════════════════════════════════
// TOP-LEVEL PAGE
// ═══════════════════════════════════════════════════════════════════
export default function LeadNurture() {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [tab, setTab] = useState('today');
  const [showNewLead, setShowNewLead] = useState(false);
  const [showOnboard, setShowOnboard] = useState(false);
  const [showGSheet, setShowGSheet] = useState(false);
  const [showClear, setShowClear] = useState(false);
  const [options, setOptions] = useState({});
  const [leads, setLeads] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activePreset, setActivePreset] = useState(null);

  const openLead = useCallback((id) => { if (id) navigate(`/lead-nurture/leads/${id}`); }, [navigate]);

  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const loadAll = useCallback(async () => {
    if (!token) return;
    try {
      const [lr, mr] = await Promise.all([
        fetch(`${API}/api/nurture/leads?limit=1000&include_graduated=true&phase=all`, auth),
        fetch(`${API}/api/nurture/command-metrics`, auth),
      ]);
      const l = await lr.json();
      const m = await mr.json();
      setLeads(Array.isArray(l) ? l : []);
      setMetrics(m || null);
    } catch (e) { toast.error('Failed to load leads'); }
    finally { setLoading(false); }
  }, [token, auth]);

  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => {
    if (!token) return;
    fetch(`${API}/api/nurture/options`, auth).then((r) => r.json()).then((d) => setOptions(d || {})).catch(() => {});
  }, [token, auth]);
  useEffect(() => { if (new URLSearchParams(window.location.search).get('gsheet')) setShowGSheet(true); }, []);

  const openLeadsWithPreset = (presetId) => { setActivePreset(presetId); setTab('leads'); };

  const TABS = [
    { key: 'today', label: 'Today', icon: Zap },
    { key: 'dashboard', label: 'Dashboard', icon: Radar },
    { key: 'leads', label: 'Leads', icon: Users },
    { key: 'pipeline', label: 'Pipeline', icon: Columns3 },
    { key: 'reports', label: 'Reports', icon: TrendingUp },
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <button type="button" onClick={() => navigate('/')} data-testid="ln-back"
          className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-slate-500 hover:text-slate-900 transition-colors mb-4">
          <ChevronLeft className="w-4 h-4" /> Back to CRM
        </button>

        <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
          <div>
            <div className="flex items-center gap-2 mb-1.5">
              <span className="w-9 h-9 rounded-2xl bg-gradient-to-br from-rose-100 to-orange-50 border border-rose-200/60 flex items-center justify-center">
                <Zap className="w-[18px] h-[18px] text-rose-600" strokeWidth={2.2} />
              </span>
              <span className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">Derwent · Lead Nurture</span>
            </div>
            <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-slate-950">Sales Command Centre</h1>
            <p className="text-sm text-slate-500 mt-1 max-w-lg">Who needs attention, what happens next, and which leads are becoming opportunities — all in one view.</p>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <button type="button" onClick={() => setShowClear(true)} data-testid="ln-clear-followups-btn"
              className="inline-flex items-center gap-1.5 rounded-full text-[12px] font-bold px-4 py-2.5 bg-white text-rose-700 ring-1 ring-rose-200 shadow-sm hover:bg-rose-50 transition-colors">
              <Eraser className="w-3.5 h-3.5" /> Clear all follow-ups
            </button>
            <button type="button" onClick={() => setShowGSheet(true)} data-testid="ln-gsheet-btn"
              className="hidden sm:inline-flex items-center gap-1.5 rounded-full text-[12px] font-bold px-4 py-2.5 bg-white text-emerald-700 ring-1 ring-emerald-200 shadow-sm hover:bg-emerald-50 transition-colors">
              <Sheet className="w-3.5 h-3.5" /> Google Sheet
            </button>
            <button type="button" onClick={() => setShowOnboard(true)} data-testid="ln-start-onboarding-btn"
              className="hidden sm:inline-flex items-center gap-1.5 rounded-full text-[12px] font-bold px-4 py-2.5 bg-indigo-600 text-white shadow-sm hover:bg-indigo-700 transition-colors">
              <ShieldCheck className="w-3.5 h-3.5" /> Start Client Portal
            </button>
            <button type="button" onClick={() => setShowNewLead(true)} data-testid="ln-add-lead-btn"
              className="inline-flex items-center gap-1.5 rounded-full text-[12px] font-bold px-4 py-2.5 bg-[#0f172a] text-white shadow-sm hover:bg-slate-800 transition-colors">
              <UserPlus className="w-3.5 h-3.5" /> Add Lead
            </button>
          </div>
        </div>

        {/* Tab switcher */}
        <div className="-mx-4 px-4 sm:mx-0 sm:px-0 overflow-x-auto no-scrollbar mb-6">
          <div className="flex gap-1 bg-white rounded-full p-1 border border-slate-200/80 shadow-sm w-max">
          {TABS.map((t) => (
            <button key={t.key} type="button" onClick={() => setTab(t.key)} data-testid={`ln-tab-${t.key}`}
              className={`relative inline-flex items-center gap-1.5 rounded-full text-[13px] font-bold px-4 sm:px-5 py-2 whitespace-nowrap transition-colors ${tab === t.key ? 'text-white' : 'text-slate-600 hover:text-slate-900'}`}>
              {tab === t.key && <motion.span layoutId="ln-tab-bg" className="absolute inset-0 rounded-full" style={{ backgroundColor: '#0f172a' }} transition={{ type: 'spring', stiffness: 340, damping: 30 }} />}
              <span className="relative z-10 flex items-center gap-1.5"><t.icon className="w-3.5 h-3.5" />{t.label}</span>
            </button>
          ))}
          </div>
        </div>

        {loading ? <PageLoader /> : (
          <AnimatePresence mode="wait">
            <motion.div key={tab} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
              {tab === 'today' && <TodayView leads={leads} metrics={metrics} onOpen={openLead} onGoto={openLeadsWithPreset} />}
              {tab === 'dashboard' && <DashboardView metrics={metrics} onMetricClick={(key) => { const p = METRIC_TO_PRESET[key]; if (p) openLeadsWithPreset(p); }} leads={leads} onOpen={openLead} />}
              {tab === 'leads' && <LeadListView leads={leads} options={options} user={user} activePreset={activePreset} setActivePreset={setActivePreset} onOpen={openLead} onAddLead={() => setShowNewLead(true)} />}
              {tab === 'pipeline' && <PipelineBoard leads={leads.filter((l) => !['graduated'].includes(l.status) && !isPreReset(l))} token={token} onOpen={openLead} onMoved={loadAll} />}
              {tab === 'reports' && <ReportsView token={token} onOpen={openLead} />}
            </motion.div>
          </AnimatePresence>
        )}

        <div className="mt-8 pt-4 border-t border-slate-200/60 text-center">
          <button type="button" onClick={() => navigate('/lead-nurture/legacy')} data-testid="ln-legacy-link"
            className="text-[11px] font-semibold text-slate-400 hover:text-slate-700 transition-colors">
            Need Templates, Sequences, Daily Summary or CSV export? → Open legacy view
          </button>
        </div>
      </main>

      {/* Mobile floating Add Lead */}
      <button type="button" onClick={() => setShowNewLead(true)} data-testid="ln-add-lead-fab" aria-label="Add lead"
        className="sm:hidden fixed bottom-24 left-4 z-40 inline-flex items-center gap-2 rounded-full pl-4 pr-5 py-3.5 bg-[#0f172a] text-white text-sm font-bold shadow-lg shadow-slate-900/25 active:scale-95 transition-transform">
        <UserPlus className="w-5 h-5" /> Add Lead
      </button>

      <StartOnboardingDialog open={showOnboard} onOpenChange={setShowOnboard} onCreated={(id) => navigate(`/onboarding/${id}`)} />
      <ClearFollowUpsDialog open={showClear} onOpenChange={setShowClear} token={token} onCleared={loadAll} />
      <GoogleSheetsDialog open={showGSheet} onClose={() => setShowGSheet(false)} token={token} />
      <NewLeadDialog open={showNewLead} onClose={() => setShowNewLead(false)} options={options} authToken={token}
        onSuccess={() => { setShowNewLead(false); loadAll(); }} />
      <Footer />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// DASHBOARD VIEW — metric tiles
// ═══════════════════════════════════════════════════════════════════
const DashboardView = ({ metrics, onMetricClick, leads, onOpen }) => {
  const priorities = useMemo(() => {
    const active = leads.filter((l) => !['lost', 'converted', 'moved_to_client_portal', 'graduated'].includes(l.status) && !isPreReset(l) && !l.no_follow_up);
    return [...active].sort((a, b) => priorityScore(b) - priorityScore(a)).slice(0, 6);
  }, [leads]);

  return (
    <div className="space-y-6">
      <div>
        <SectionTitle icon={Radar} eyebrow="Live pulse" title="Your pipeline at a glance" />
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 mt-3">
          {METRICS.map((m, i) => (
            <motion.button key={m.key} type="button"
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.02 }}
              onClick={() => onMetricClick(m.key)} data-testid={`metric-card-${m.key}`}
              className="text-left rounded-2xl bg-white border border-slate-200/80 p-3.5 shadow-[0_2px_8px_-4px_rgba(15,23,42,0.05)] hover:shadow-[0_12px_32px_-16px_rgba(15,23,42,0.18)] hover:-translate-y-0.5 transition-[transform,box-shadow] focus:outline-none focus:ring-2 focus:ring-rose-200">
              <p className={`text-3xl font-black tracking-tight font-mono ${COLOR_TONE[m.color]}`}>{metrics?.[m.key] ?? 0}</p>
              <p className="text-[11px] font-bold text-slate-500 mt-1 leading-tight">{m.label}</p>
            </motion.button>
          ))}
        </div>
      </div>

      <div>
        <SectionTitle icon={Sparkles} eyebrow="DerwentAI priority" title="Work these leads first" />
        <div className="grid grid-cols-1 gap-3 mt-3">
          {priorities.length === 0 ? (
            <div className="rounded-2xl bg-white border border-slate-200/80 p-8 text-center text-sm text-slate-500">No active leads to prioritise.</div>
          ) : priorities.map((l) => <LeadCard key={l.id} lead={l} onOpen={() => onOpen(l.id)} />)}
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// TODAY VIEW — main working screen
// ═══════════════════════════════════════════════════════════════════
const TodayView = ({ leads, metrics, onOpen, onGoto }) => {
  const groups = useMemo(() => {
    const active = leads.filter((l) => !['lost', 'converted', 'moved_to_client_portal', 'graduated'].includes(l.status) && !isPreReset(l) && !l.no_follow_up);
    const returned = (l) => nurtureReturned(l);
    const parked = (l) => !returned(l) && (l.status === 'long_term_nurture' || (l.nurture_until && new Date(l.nurture_until) > new Date()));
    const live = active.filter((l) => !parked(l));
    const fu = (l) => l.next_follow_up_at || l.next_action_due;
    const repliedRecently = (l) => { const t = l.last_client_response_at || l.last_response_at; return t && (Date.now() - new Date(t).getTime()) <= 2 * 86400000; };
    return {
      overdue: live.filter((l) => fu(l) && daysUntil(fu(l)) < 0),
      due_today: live.filter((l) => fu(l) && daysUntil(fu(l)) === 0),
      replied: live.filter(repliedRecently),
      new_leads: live.filter((l) => !l.first_contacted_at),
      hot_opps: live.filter((l) => effectiveTemp(l) === 'hot'),
      portal_activity: live.filter((l) => l.portal_sent_at && !l.portal_completed_at),
      nurture_due: active.filter(returned),
    };
  }, [leads]);

  const SECTIONS = [
    { id: 'overdue', label: 'Overdue', icon: AlertCircle, tone: 'red' },
    { id: 'due_today', label: 'Due Today', icon: Clock, tone: 'amber' },
    { id: 'nurture_due', label: 'Nurture Due — Re-engage', icon: Rocket, tone: 'rose' },
    { id: 'replied', label: 'Clients Who Replied', icon: Reply, tone: 'emerald' },
    { id: 'new_leads', label: 'New Uncontacted Leads', icon: UserPlus, tone: 'blue' },
    { id: 'hot_opps', label: 'Hot Opportunities', icon: Flame, tone: 'rose' },
    { id: 'portal_activity', label: 'Portal Activity', icon: ExternalLink, tone: 'purple' },
  ];
  const DOT = { red: 'bg-red-500', amber: 'bg-amber-500', emerald: 'bg-emerald-500', blue: 'bg-blue-500', rose: 'bg-rose-500', purple: 'bg-purple-500' };
  const totalTodo = groups.overdue.length + groups.due_today.length + groups.new_leads.length + groups.nurture_due.length;

  return (
    <div className="space-y-6">
      {/* Hero */}
      <motion.section initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}
        className="brand-dark relative overflow-hidden rounded-3xl bg-slate-950 text-white shadow-[0_20px_60px_-15px_rgba(2,6,23,0.4)]" data-testid="today-hero">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-rose-500/20 blur-3xl" aria-hidden />
        <div className="relative p-6 sm:p-8">
          <div className="flex items-center gap-2 mb-2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 backdrop-blur px-3 py-1 text-[10px] font-black uppercase tracking-[0.2em] text-white/90 ring-1 ring-white/15">
              <CalendarDays className="w-3 h-3" /> Today
            </span>
            <span className="text-[11px] font-semibold text-white/70">{new Date().toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long' })}</span>
          </div>
          <h2 className="text-white text-2xl sm:text-3xl font-black tracking-tight leading-[1.1]">
            {totalTodo > 0 ? <>{totalTodo} lead{totalTodo === 1 ? '' : 's'} need <span className="text-rose-300">you today.</span></> : <>You&apos;re on top of it. <span className="text-emerald-300">Beautiful.</span></>}
          </h2>
          <div className="mt-6 grid grid-cols-3 sm:grid-cols-6 gap-2.5">
            {SECTIONS.map((s) => (
              <a key={s.id} href={`#today-${s.id}`} className="rounded-2xl p-3 bg-white/[0.10] hover:bg-white/[0.18] ring-1 ring-white/15 transition-colors">
                <p className="text-2xl font-black font-mono leading-none">{groups[s.id].length}</p>
                <p className="text-[10px] font-bold text-white/80 mt-1 leading-tight">{s.label}</p>
              </a>
            ))}
          </div>
        </div>
      </motion.section>

      {SECTIONS.map((s) => (
        <section key={s.id} id={`today-${s.id}`} data-testid={`today-section-${s.id}`}>
          <div className="flex items-center gap-2 mb-3">
            <span className={`w-2 h-2 rounded-full ${DOT[s.tone]}`} />
            <s.icon className="w-4 h-4 text-slate-600" strokeWidth={2.2} />
            <h3 className="text-base font-black text-slate-900">{s.label}</h3>
            <span className="text-xs font-bold text-slate-400">{groups[s.id].length}</span>
          </div>
          {groups[s.id].length === 0 ? (
            <p className="text-sm text-slate-400 pl-4">Nothing here right now.</p>
          ) : (
            <motion.div initial="hidden" animate="show" variants={{ hidden: {}, show: { transition: { staggerChildren: 0.03 } } }} className="grid grid-cols-1 gap-3">
              {groups[s.id].slice(0, 12).map((l) => <LeadCard key={l.id} lead={l} onOpen={() => onOpen(l.id)} />)}
            </motion.div>
          )}
        </section>
      ))}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// LEADS LIST VIEW — search + presets + filters + sort
// ═══════════════════════════════════════════════════════════════════
const LeadListView = ({ leads, options, user, activePreset, setActivePreset, onOpen, onAddLead }) => {
  const [q, setQ] = useState('');
  const [sortPriority, setSortPriority] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ owner: '', source: '', status: '', temp: '', purpose: '', portal: '' });

  const owners = useMemo(() => [...new Set(leads.map((l) => l.assigned_broker_name).filter(Boolean))], [leads]);
  const sources = useMemo(() => [...new Set(leads.map((l) => l.lead_source).filter(Boolean))], [leads]);
  const purposes = useMemo(() => [...new Set(leads.map((l) => l.loan_purpose).filter(Boolean))], [leads]);

  const filtered = useMemo(() => {
    let rows = leads;
    // Keep Historical / Pre-Reset leads out of the active workspace unless the
    // user explicitly views them (Historical preset) or searches. They stay in
    // the dataset so search + the Historical filter can always surface them.
    if (activePreset !== 'historical' && !q) rows = rows.filter((l) => !isPreReset(l));
    if (activePreset) rows = rows.filter(presetPredicate(activePreset, user?.id));
    if (q) { const s = q.toLowerCase(); rows = rows.filter((l) => l.name?.toLowerCase().includes(s) || l.email?.toLowerCase().includes(s) || (l.phone || '').includes(q) || l.lead_source?.toLowerCase().includes(s) || (l.notes || '').toLowerCase().includes(s)); }
    if (filters.owner) rows = rows.filter((l) => l.assigned_broker_name === filters.owner);
    if (filters.source) rows = rows.filter((l) => l.lead_source === filters.source);
    if (filters.status) rows = rows.filter((l) => l.status === filters.status);
    if (filters.temp) rows = rows.filter((l) => effectiveTemp(l) === filters.temp);
    if (filters.purpose) rows = rows.filter((l) => l.loan_purpose === filters.purpose);
    if (filters.portal === 'sent') rows = rows.filter((l) => l.portal_sent_at && !l.portal_completed_at);
    if (filters.portal === 'started') rows = rows.filter((l) => l.portal_opened_at || l.portal_started_at);
    if (filters.portal === 'completed') rows = rows.filter((l) => l.portal_completed_at);
    if (filters.portal === 'none') rows = rows.filter((l) => !l.portal_sent_at);
    const arr = [...rows];
    arr.sort((a, b) => sortPriority ? priorityScore(b) - priorityScore(a) : new Date(b.created_at) - new Date(a.created_at));
    return arr;
  }, [leads, activePreset, q, filters, sortPriority, user]);

  const hasFilters = activePreset || Object.values(filters).some(Boolean) || q;
  const clearAll = () => { setActivePreset(null); setFilters({ owner: '', source: '', status: '', temp: '', purpose: '', portal: '' }); setQ(''); };

  const selCls = 'rounded-xl text-xs font-semibold bg-white border border-slate-200 px-3 py-2 outline-none focus:ring-2 focus:ring-rose-100';

  return (
    <div className="space-y-4">
      {/* Search + controls */}
      <div className="flex items-center gap-2">
        <div className="flex-1 relative">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input type="text" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, phone, email, source, notes…"
            data-testid="ln-search" className="w-full pl-10 pr-3 py-3 rounded-2xl outline-none text-sm bg-white border border-slate-200/80 focus:border-slate-300 focus:ring-2 focus:ring-rose-100 shadow-[0_2px_8px_-4px_rgba(15,23,42,0.06)]" />
        </div>
        <button type="button" onClick={() => setSortPriority((v) => !v)} data-testid="ln-sort-priority"
          className={`inline-flex items-center gap-1.5 rounded-2xl text-[12px] font-bold px-3.5 py-3 border transition-colors ${sortPriority ? 'bg-violet-600 text-white border-violet-600' : 'bg-white text-slate-800 border-slate-200/80 hover:border-slate-300'}`} title="Sort by DerwentAI priority">
          <ArrowDownUp className="w-3.5 h-3.5" /> <span className="hidden sm:inline">{sortPriority ? 'AI Priority' : 'Newest'}</span>
        </button>
        <button type="button" onClick={() => setShowFilters((v) => !v)} data-testid="ln-toggle-filters"
          className={`inline-flex items-center gap-1.5 rounded-2xl text-[12px] font-bold px-3.5 py-3 border transition-colors ${showFilters ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-800 border-slate-200/80 hover:border-slate-300'}`}>
          <SlidersHorizontal className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Filters</span>
        </button>
      </div>

      {/* Preset chips */}
      <div className="flex gap-1.5 flex-wrap">
        <PresetChip label="My Leads" active={activePreset === 'my_leads'} onClick={() => setActivePreset(activePreset === 'my_leads' ? null : 'my_leads')} testid="filter-chip-my-leads" />
        {PRESETS.map((p) => (
          <PresetChip key={p.id} label={p.label} active={activePreset === p.id} onClick={() => setActivePreset(activePreset === p.id ? null : p.id)} testid={`filter-chip-${p.id}`} />
        ))}
      </div>

      {/* Filter panel */}
      <AnimatePresence>
        {showFilters && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
            <div className="rounded-2xl p-3 bg-white border border-slate-200/80 flex flex-wrap gap-2">
              <select value={filters.owner} onChange={(e) => setFilters({ ...filters, owner: e.target.value })} className={selCls} data-testid="filter-owner"><option value="">Any owner</option>{owners.map((o) => <option key={o} value={o}>{o}</option>)}</select>
              <select value={filters.source} onChange={(e) => setFilters({ ...filters, source: e.target.value })} className={selCls} data-testid="filter-source"><option value="">Any source</option>{sources.map((o) => <option key={o} value={o}>{o}</option>)}</select>
              <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className={selCls} data-testid="filter-status"><option value="">Any status</option>{Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
              <select value={filters.temp} onChange={(e) => setFilters({ ...filters, temp: e.target.value })} className={selCls} data-testid="filter-temp"><option value="">Any temp</option><option value="hot">Hot</option><option value="warm">Warm</option><option value="nurture">Nurture</option><option value="cold">Cold</option></select>
              {purposes.length > 0 && <select value={filters.purpose} onChange={(e) => setFilters({ ...filters, purpose: e.target.value })} className={selCls} data-testid="filter-purpose"><option value="">Any purpose</option>{purposes.map((o) => <option key={o} value={o}>{o}</option>)}</select>}
              <select value={filters.portal} onChange={(e) => setFilters({ ...filters, portal: e.target.value })} className={selCls} data-testid="filter-portal"><option value="">Any portal</option><option value="none">Not sent</option><option value="sent">Sent</option><option value="started">Started</option><option value="completed">Completed</option></select>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="flex items-center justify-between">
        <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{filtered.length} lead{filtered.length === 1 ? '' : 's'}{activePreset ? ` · ${[...PRESETS, { id: 'my_leads', label: 'My Leads' }].find((p) => p.id === activePreset)?.label}` : ''}</p>
        {hasFilters && <button type="button" onClick={clearAll} data-testid="ln-clear-filters" className="inline-flex items-center gap-1 text-[11px] font-bold text-slate-500 hover:text-slate-900"><X className="w-3 h-3" /> Clear</button>}
      </div>

      {filtered.length === 0 ? (
        <div className="rounded-3xl p-12 text-center bg-white border border-slate-200/80">
          <Users className="w-10 h-10 mx-auto text-slate-300 mb-3" />
          <p className="text-lg font-black text-slate-900">No leads match</p>
          <p className="text-sm text-slate-500 mt-1">Adjust your filters, or capture a new lead.</p>
          <button type="button" onClick={onAddLead} data-testid="ln-empty-add-lead" className="mt-4 inline-flex items-center gap-1.5 rounded-full text-[12px] font-bold px-4 py-2.5 bg-[#0f172a] text-white shadow-sm hover:bg-slate-800 transition-colors"><UserPlus className="w-3.5 h-3.5" /> Add Lead</button>
        </div>
      ) : (
        <motion.div initial="hidden" animate="show" variants={{ hidden: {}, show: { transition: { staggerChildren: 0.02 } } }} className="grid grid-cols-1 gap-3">
          {filtered.slice(0, 200).map((l) => <LeadCard key={l.id} lead={l} onOpen={() => onOpen(l.id)} />)}
        </motion.div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// REPORTS VIEW — KPIs + conversion funnel + source leaderboard + live feed
// ═══════════════════════════════════════════════════════════════════
const FEED_ICON = { call: PhoneCall, sms: MessageSquare, email: Mail, note: StickyNote, status_change: ArrowRight, follow_up_completed: CheckCircle2, follow_up_scheduled: CalendarDays, sequence_started: Zap };
const FUNNEL_COLORS = ['bg-slate-400', 'bg-blue-500', 'bg-violet-500', 'bg-amber-500', 'bg-orange-500', 'bg-emerald-500'];

const ReportsView = ({ token, onOpen }) => {
  const [kpis, setKpis] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [feed, setFeed] = useState([]);
  const [loading, setLoading] = useState(true);
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  useEffect(() => {
    Promise.all([
      fetch(`${API}/api/nurture/kpis`, auth).then((r) => r.json()).catch(() => ({})),
      fetch(`${API}/api/nurture/funnel`, auth).then((r) => r.json()).catch(() => null),
      fetch(`${API}/api/nurture/activity-feed?limit=30`, auth).then((r) => r.json()).catch(() => []),
    ]).then(([k, f, a]) => { setKpis(k || {}); setFunnel(f); setFeed(Array.isArray(a) ? a : []); }).finally(() => setLoading(false));
  }, [token]); // eslint-disable-line
  if (loading) return <PageLoader />;

  const cards = [
    { label: 'Contacts this month', value: kpis?.contacts_month ?? 0, icon: PhoneCall, color: 'blue' },
    { label: 'Leads captured', value: kpis?.leads_this_month ?? 0, icon: Users, color: 'indigo' },
    { label: 'Discovery calls done', value: kpis?.discoveries_month ?? 0, icon: Target, color: 'emerald' },
    { label: 'Graduated to broker', value: kpis?.graduated_month ?? 0, icon: Rocket, color: 'amber' },
  ];
  const maxFunnel = funnel?.stages?.[0]?.count || 1;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {cards.map((c) => (
          <div key={c.label} className="rounded-2xl bg-white border border-slate-200/80 p-4 shadow-[0_2px_8px_-4px_rgba(15,23,42,0.05)]">
            <c.icon className={`w-5 h-5 ${COLOR_TONE[c.color]} mb-3`} />
            <p className="text-2xl sm:text-3xl font-black tracking-tight text-slate-900 font-mono">{c.value}</p>
            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mt-1">{c.label}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
        {/* Conversion funnel */}
        <div className="lg:col-span-3 rounded-3xl bg-white border border-slate-200/80 p-5 sm:p-6 shadow-[0_2px_8px_-4px_rgba(15,23,42,0.05)]">
          <SectionTitle icon={TrendingUp} eyebrow="Conversion" title="Lead → Application funnel" />
          {funnel ? (
            <div className="mt-4 space-y-2.5" data-testid="funnel-chart">
              {funnel.stages.map((st, i) => (
                <div key={st.key} data-testid={`funnel-stage-${st.key}`}>
                  <div className="flex items-center justify-between text-[12px] mb-1">
                    <span className="font-bold text-slate-700">{st.label}</span>
                    <span className="font-mono text-slate-500"><b className="text-slate-900">{st.count}</b> · {st.conversion}%</span>
                  </div>
                  <div className="h-7 bg-slate-100 rounded-lg overflow-hidden">
                    <motion.div initial={{ width: 0 }} animate={{ width: `${Math.max(3, (st.count / maxFunnel) * 100)}%` }} transition={{ duration: 0.6, delay: i * 0.05 }}
                      className={`h-full rounded-lg ${FUNNEL_COLORS[i]}`} />
                  </div>
                </div>
              ))}
              <p className="text-[11px] text-slate-400 pt-1">Overall conversion <b className="text-slate-700 font-mono">{funnel.overall_conversion}%</b> · {funnel.total_converted}/{funnel.total_leads} leads to application</p>
              {funnel.velocity && (
                <div className="mt-4 pt-4 border-t border-slate-100" data-testid="funnel-velocity">
                  <p className="text-[10px] font-black uppercase tracking-[0.15em] text-slate-400 mb-2">Average velocity (days)</p>
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                    {[
                      ['Lead→Contact', funnel.velocity.lead_to_contacted],
                      ['→Discovery', funnel.velocity.contacted_to_discovery],
                      ['→Portal', funnel.velocity.discovery_to_portal],
                      ['→Application', funnel.velocity.portal_to_application],
                      ['Lead→App', funnel.velocity.lead_to_application],
                    ].map(([lbl, v]) => (
                      <div key={lbl} className="rounded-xl bg-slate-50 ring-1 ring-slate-200/60 p-2.5 text-center">
                        <p className="text-lg font-black font-mono text-slate-900">{v ?? '—'}</p>
                        <p className="text-[9px] font-bold uppercase tracking-wider text-slate-400 mt-0.5">{lbl}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : <p className="text-sm text-slate-400 mt-4">No funnel data.</p>}

          {/* Source leaderboard */}
          {funnel?.sources?.length > 0 && (
            <div className="mt-6">
              <p className="text-[10px] font-black uppercase tracking-[0.15em] text-slate-400 mb-2">Top converting sources</p>
              <div className="space-y-1.5" data-testid="source-leaderboard">
                {funnel.sources.slice(0, 6).map((s) => (
                  <div key={s.source} className="flex items-center gap-3 text-[12px]">
                    <span className="font-semibold text-slate-700 w-28 truncate">{s.source}</span>
                    <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${Math.min(100, s.rate)}%` }} />
                    </div>
                    <span className="font-mono text-slate-500 w-20 text-right">{s.converted}/{s.leads} · {s.rate}%</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Staff leaderboard */}
          {funnel?.staff?.length > 0 && (
            <div className="mt-6">
              <p className="text-[10px] font-black uppercase tracking-[0.15em] text-slate-400 mb-2">Staff conversion leaderboard</p>
              <div className="space-y-1.5" data-testid="staff-leaderboard">
                {funnel.staff.slice(0, 6).map((s) => (
                  <div key={s.staff} className="flex items-center gap-3 text-[12px]">
                    <span className="font-semibold text-slate-700 w-28 truncate">{s.staff}</span>
                    <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-violet-500 rounded-full" style={{ width: `${Math.min(100, s.rate)}%` }} />
                    </div>
                    <span className="font-mono text-slate-500 w-24 text-right">{s.converted}/{s.leads} · {s.rate}%</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="lg:col-span-2 rounded-3xl bg-white border border-slate-200/80 p-5 sm:p-6 shadow-[0_2px_8px_-4px_rgba(15,23,42,0.05)]">
          <SectionTitle icon={Activity} eyebrow="Live" title="Team activity feed" />
          {feed.length === 0 ? (
            <p className="text-sm text-slate-400 mt-4">No recent activity.</p>
          ) : (
            <ul className="mt-4 space-y-0 max-h-[460px] overflow-y-auto" data-testid="activity-feed">
              {feed.map((a, i) => {
                const Icon = FEED_ICON[a.activity_type] || Activity;
                return (
                  <li key={a.id || i}>
                    <button type="button" onClick={() => a.lead_id && onOpen(a.lead_id)} data-testid={`feed-item-${i}`}
                      className="w-full text-left relative flex gap-3 pb-4 group">
                      {i < feed.length - 1 && <span className="absolute left-[15px] top-8 bottom-0 w-px bg-slate-200" aria-hidden />}
                      <span className="flex-shrink-0 w-8 h-8 rounded-full bg-slate-50 ring-1 ring-slate-200 flex items-center justify-center group-hover:ring-rose-200">
                        <Icon className="w-3.5 h-3.5 text-slate-500" />
                      </span>
                      <div className="min-w-0 flex-1 pt-0.5">
                        <p className="text-[12px] text-slate-800 leading-snug"><b className="font-bold">{a.lead_name}</b> — {a.description}</p>
                        <p className="text-[10px] text-slate-400 mt-0.5">{a.broker ? `${a.broker} · ` : ''}{fmtRel(a.created_at)}</p>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// SHARED
// ═══════════════════════════════════════════════════════════════════
const PresetChip = ({ label, active, onClick, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`text-[12px] font-bold px-3 py-1.5 rounded-full transition-colors ${active ? 'bg-rose-600 text-white shadow-sm' : 'bg-white text-slate-600 ring-1 ring-slate-200 hover:ring-slate-400 hover:text-slate-900'}`}>
    {label}
  </button>
);
const SectionTitle = ({ icon: Icon, eyebrow, title }) => (
  <div>
    <div className="flex items-center gap-2"><Icon className="w-3.5 h-3.5 text-slate-500" strokeWidth={2.4} /><span className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">{eyebrow}</span></div>
    <p className="text-lg sm:text-xl font-black text-slate-900 mt-0.5">{title}</p>
  </div>
);
const PageLoader = () => (
  <div className="rounded-3xl p-16 bg-white border border-slate-200/80 text-center"><Loader2 className="w-6 h-6 animate-spin inline-block text-slate-400" /></div>
);
